import React, { useState, useEffect } from 'react';
import {
    X,
    AlertCircle,
    Plus,
    RefreshCw,
    Save
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
    sanitizeTextInput,
    validateNumberInput,
    FIELD_LIMITS
} from '../../../utils/inputValidation';
import { companySizeService, leadSourceService } from '../../../api/crm/config/configService';

const QualificationCriteriaModal = ({
    isOpen,
    onClose,
    selectedCriteria,
    onSave,
    isSaving,
    criteriaFormData,
    setCriteriaFormData,
    criteriaErrors,
    setCriteriaErrors
}) => {
    const [newCriterion, setNewCriterion] = useState({ field: 'budget', operator: 'greater_than', value: '' });
    const [companySizeOptions, setCompanySizeOptions] = useState([]);
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);

    useEffect(() => {
        const fetchLeadSources = async () => {
            try {
                const response = await leadSourceService.getAll({ isActive: true });
                if (response.success && Array.isArray(response.data)) {
                    const options = response.data.map(source => ({
                        value: source.name,
                        label: source.name
                            .split(/_|\s+/)
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                    }));
                    setLeadSourceOptions(options);
                }
            } catch (error) {
            }
        };
        const fetchCompanySizes = async () => {
            try {
                const response = await companySizeService.getAll({ isActive: true });
                if (response.success && Array.isArray(response.data)) {
                    const options = response.data.map(size => ({
                        value: size._id,
                        label: size.name
                    }));
                    setCompanySizeOptions(options);
                }
            } catch (error) {
            }
        };
        fetchLeadSources();
        fetchCompanySizes();
    }, []);

    // Predefine field options
    const fieldOptions = {
        source: {
            label: 'Source',
            type: 'enum',
            options: leadSourceOptions,
            operators: ['equals', 'not_equals']
        },
        region: {
            label: 'Region',
            type: 'enum',
            options: [
                { value: 'North', label: 'North' },
                { value: 'South', label: 'South' },
                { value: 'East', label: 'East' },
                { value: 'West', label: 'West' },
                { value: 'Central', label: 'Central' }
            ],
            operators: ['equals', 'not_equals']
        },
        companySize: {
            label: 'Company Size',
            type: 'enum',
            options: companySizeOptions,
            operators: ['equals', 'not_equals']
        },
        budget: {
            label: 'Budget',
            type: 'number',
            operators: ['greater_than', 'less_than', 'equals']
        },
        designation: {
            label: 'Role',
            type: 'enum',
            options: [
                { value: 'CEO/Founder', label: 'CEO/Founder' },
                { value: 'CTO', label: 'CTO' },
                { value: 'CFO', label: 'CFO' },
                { value: 'VP/Director', label: 'VP/Director' },
                { value: 'Manager', label: 'Manager' },
                { value: 'Executive', label: 'Executive' },
                { value: 'Other', label: 'Other' }
            ],
            operators: ['equals', 'not_equals']
        },
        timeline: {
            label: 'Timeline',
            type: 'enum',
            options: [
                { value: 'Immediate (0-30 days)', label: 'Immediate (0-30 days)' },
                { value: 'Short-term (1-3 months)', label: 'Short-term (1-3 months)' },
                { value: 'Medium-term (3-6 months)', label: 'Medium-term (3-6 months)' },
                { value: 'Long-term (6+ months)', label: 'Long-term (6+ months)' }
            ],
            operators: ['equals', 'not_equals']
        }
    };

    const getCurrentCriteriaFieldConfig = () => {
        // Map 'role' to 'designation' for qualification criteria
        const field = newCriterion.field === 'role' ? 'designation' : newCriterion.field;
        return fieldOptions[field] || { type: 'text', operators: ['equals', 'contains'], label: newCriterion.field };
    };

    const formatOperator = (operator) => {
        const labels = {
            'equals': 'Equals',
            'not_equals': 'Not Equals',
            'contains': 'Contains',
            'in': 'In (Multiple)',
            'not_in': 'Not In',
            'greater_than': 'Greater Than',
            'less_than': 'Less Than',
            'greater_than_equal': 'Greater Than or Equal',
            'less_than_equal': 'Less Than or Equal',
            'between': 'Between'
        };
        return labels[operator] || operator?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || operator;
    };

    const formatValue = (field, value) => {
        if (!value) return '';
        if (typeof value === 'number') return String(value);

        const fieldConfig = fieldOptions[field];
        if (fieldConfig?.options) {
            if (field === 'companySize') {
                const option = fieldConfig.options.find(opt => 
                    String(opt.value) === String(value) || 
                    String(opt.label).toLowerCase() === String(value).toLowerCase()
                );
                if (option) return option.label;
            } else {
                const option = fieldConfig.options.find(opt => String(opt.value) === String(value));
                if (option) return option.label;
            }
        }
        return String(value).split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const handleCriteriaFieldChange = (field) => {
        // Map 'role' to 'designation' for fieldOptions lookup
        const lookupField = field === 'role' ? 'designation' : field;
        const fieldConfig = fieldOptions[lookupField];
        setNewCriterion({
            field,
            operator: fieldConfig?.operators[0] || 'equals',
            value: ''
        });
    };

    const handleAddCriterion = () => {
        // Reset specific new criterion error
        setCriteriaErrors(prev => ({ ...prev, newCriterion: undefined }));

        if (newCriterion.value) {
            // Map 'role' to 'designation' for backend compatibility
            const criterionToAdd = {
                ...newCriterion,
                field: newCriterion.field === 'role' ? 'designation' : newCriterion.field
            };
            // Remove label from criterion object
            delete criterionToAdd.label;
            setCriteriaFormData(prev => ({
                ...prev,
                criteria: [...prev.criteria, criterionToAdd]
            }));

            // Clear specific new rule error and generic criteria error
            setCriteriaErrors(prev => ({
                ...prev,
                criteria: undefined,
                newCriterion: undefined
            }));

            // Reset with default operator for budget field
            const budgetConfig = fieldOptions.budget;
            setNewCriterion({
                field: 'budget',
                operator: budgetConfig?.operators[0] || 'greater_than',
                value: ''
            });
        } else {
            setCriteriaErrors(prev => ({
                ...prev,
                newCriterion: 'Please select a value.'
            }));
        }
    };

    const handleRemoveCriterion = (index) => {
        setCriteriaFormData(prev => ({
            ...prev,
            criteria: prev.criteria.filter((_, i) => i !== index)
        }));
    };

    const handleCriteriaFormDataChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
        let processedValue = value;

        if (fieldType === 'textarea') {
            const temp = sanitizeTextInput(String(value || ''));
            // Additional custom sanitization for description if needed
            // The user code had `sanitizeDescriptionInput`, assuming it handles punctuation.
            // But we can reuse sanitizeTextInput for now as a base.
            // If sanitizeDescriptionInput exists, import it. Does it?
            // Let's stick with sanitizeTextInput but remove < and > explicitly if needed.
            // For now duplicating logic:
            processedValue = temp.replace(/[<>]/g, '');
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'text') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        } else if (typeof value === 'boolean') {
            processedValue = value;
        }

        setCriteriaFormData(prev => ({
            ...prev,
            [field]: processedValue
        }));
    };

    // Handle blur for validation
    const handleBlur = (field) => {
        const newErrors = { ...criteriaErrors };

        if (field === 'name') {
            if (!criteriaFormData.name.trim()) {
                newErrors.name = 'Criteria name is required.';
            } else {
                delete newErrors.name;
            }
        }

        if (field === 'criteria') {
            if (criteriaFormData.criteria.length === 0) {
                newErrors.criteria = 'Please add at least one qualifying criterion.';
            } else {
                delete newErrors.criteria;
            }
        }

        setCriteriaErrors(newErrors);
    };

    // Helper for description sanitization
    const sanitizeDescriptionInput = (input) => {
        // Allow basic punctuation used in descriptions but prevent HTML
        return input.replace(/[<>]/g, '');
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {selectedCriteria ? 'Edit Qualification Criteria' : 'Create Qualification Criteria'}
                    </h3>
                    <button
                        onClick={() => {
                            onClose();
                            setCriteriaErrors({});
                        }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Criteria Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={criteriaFormData.name}
                            onChange={(e) => handleCriteriaFormDataChange('name', e.target.value)}
                            onBlur={() => handleBlur('name')}
                            maxLength={100}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${criteriaErrors.name ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="Enter criteria name"
                        />
                        <div className="flex justify-between mt-1 items-center">
                            <div>
                                {criteriaErrors.name && (
                                    <p className="text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {criteriaErrors.name}
                                    </p>
                                )}
                            </div>
                            <div>
                                <span className="text-xs text-gray-500">
                                    {criteriaFormData.name.length}/100 characters
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            value={criteriaFormData.description}
                            onChange={(e) => {
                                const processed = sanitizeDescriptionInput(e.target.value || '');
                                const limited = processed.slice(0, FIELD_LIMITS.notes || 1000);
                                handleCriteriaFormDataChange('description', limited, 'textarea', FIELD_LIMITS.notes);
                            }}
                            maxLength={1000}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${criteriaErrors.description ? 'border-red-300' : 'border-gray-300'
                                }`}
                            placeholder="Enter criteria description"
                        />
                        <div className="flex justify-between mt-1 items-center">
                            <div>
                                {criteriaErrors.description && (
                                    <p className="text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {criteriaErrors.description}
                                    </p>
                                )}
                            </div>
                            <div>
                                <span className="text-xs text-gray-500">
                                    {criteriaFormData.description.length}/1000 characters
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Qualification Criteria <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-3" onBlur={() => handleBlur('criteria')}>
                            {criteriaErrors.criteria && (
                                <p className="text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {criteriaErrors.criteria}
                                </p>
                            )}
                            {/* Existing criteria */}
                            {criteriaFormData.criteria.map((criterion, index) => {
                                // Map 'designation' to 'role' for display
                                const displayField = criterion.field === 'designation' ? 'role' : criterion.field;
                                const fieldLabel = fieldOptions[displayField]?.label || displayField;
                                return (
                                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm font-medium text-gray-700 capitalize">{fieldLabel}</span>
                                        <span className="text-sm text-gray-500">{formatOperator(criterion.operator)}</span>
                                        <span className="text-sm text-gray-700 capitalize">{formatValue(criterion.field, criterion.value)}</span>
                                        <button
                                            onClick={() => handleRemoveCriterion(index)}
                                            className="text-red-500 hover:text-red-700 ml-auto"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                );
                            })}

                            {/* Add new criterion */}
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <select
                                        value={newCriterion.field}
                                        onChange={(e) => handleCriteriaFieldChange(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    >
                                        <option value="source">Source</option>
                                        <option value="region">Region</option>
                                        <option value="companySize">Company Size</option>
                                        <option value="budget">Budget</option>
                                        <option value="role">Role</option>
                                        <option value="timeline">Timeline</option>
                                    </select>
                                    <select
                                        value={newCriterion.operator}
                                        onChange={(e) => setNewCriterion(prev => ({ ...prev, operator: e.target.value, value: '' }))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    >
                                        {getCurrentCriteriaFieldConfig().operators.map(op => (
                                            <option key={op} value={op}>{formatOperator(op)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-center space-x-3">
                                    {getCurrentCriteriaFieldConfig().type === 'enum' ? (
                                        <select
                                            value={newCriterion.value}
                                            onChange={(e) => setNewCriterion(prev => ({ ...prev, value: e.target.value }))}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        >
                                            <option value="">Select {getCurrentCriteriaFieldConfig().label}</option>
                                            {getCurrentCriteriaFieldConfig().options
                                                .filter(option =>
                                                    !criteriaFormData.criteria.some(c =>
                                                        c.field === (newCriterion.field === 'role' ? 'designation' : newCriterion.field) &&
                                                        c.operator === newCriterion.operator &&
                                                        String(c.value) === String(option.value)
                                                    )
                                                )
                                                .map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                        </select>
                                    ) : getCurrentCriteriaFieldConfig().type === 'number' ? (
                                        <input
                                            type="number"
                                            value={newCriterion.value}
                                            onChange={(e) => {
                                                const numValidation = validateNumberInput(e.target.value, Number.MAX_SAFE_INTEGER, 0);
                                                setNewCriterion(prev => ({ ...prev, value: numValidation.value || '' }));
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                            placeholder={`Enter ${getCurrentCriteriaFieldConfig().label.toLowerCase()}`}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={newCriterion.value}
                                            onChange={(e) => {
                                                const sanitized = sanitizeTextInput(e.target.value);
                                                setNewCriterion(prev => ({ ...prev, value: sanitized.slice(0, 100) }));
                                            }}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                            placeholder={`Enter ${getCurrentCriteriaFieldConfig().label.toLowerCase()}`}
                                            maxLength={100}
                                        />
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Plus className="h-4 w-4" />}
                                        onClick={handleAddCriterion}
                                        className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                    >
                                        Add
                                    </Button>
                                </div>

                                {/* Inline Error for New Criterion */}
                                {criteriaErrors.newCriterion && (
                                    <div className="flex items-center mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200">
                                        <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                                        <span>{criteriaErrors.newCriterion}</span>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 mt-5">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="criteriaIsActive"
                                checked={criteriaFormData.isActive}
                                onChange={(e) => handleCriteriaFormDataChange('isActive', e.target.checked, 'boolean')}
                                className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                            />
                            <label htmlFor="criteriaIsActive" className="ml-2 block text-sm text-gray-700">
                                Active Criteria
                            </label>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Qualification Logic
                            </label>
                            <select
                                value={criteriaFormData.qualificationLogic || 'all'}
                                onChange={(e) => handleCriteriaFormDataChange('qualificationLogic', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                            >
                                <option value="all">All criteria must match (AND)</option>
                                <option value="any">Any criteria can match (OR)</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onClose();
                            setCriteriaErrors({});
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        icon={isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        onClick={onSave}
                        disabled={isSaving || !criteriaFormData.name.trim() || criteriaFormData.criteria.length === 0}
                    >
                        {isSaving
                            ? (selectedCriteria ? 'Updating...' : 'Creating...')
                            : (selectedCriteria ? 'Update Criteria' : 'Create Criteria')
                        }
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default QualificationCriteriaModal;
